<template>
    <Fixed
        class="gallery-modal"
        @close="close"
    >
        <ModalWithHeader
            class="modal-with-header"
            @close="close"
        >
            <template #header>
                <div
                    v-if="threeSixty.length > 0"
                    class="switch"
                >
                    <button
                        class="button"
                        type="button"
                        @click="toggleViewer"
                    >
                        {{
                            isThreeSixtyVisible
                                ? t('gallery.view_gallery')
                                : t('gallery.view_360')
                        }}
                    </button>
                </div>
            </template>

            <template #default>
                <div class="viewer">
                    <div
                        class="release-to-close"
                        :style="`opacity: ${willClose ? 0.6 : 0}; ${willClose ? 'z-index: unset' : ''};`"
                    >
                        <div class="text">Release to close</div>
                    </div>

                    <div
                        ref="container"
                        class="carousel"
                        :style="styleContainer"
                    >
                        <ThVideo
                            v-if="video"
                            :video
                            autoplay
                            enable-tap-to-play
                            class="slide"
                            :class="{ zoomed }"
                        />

                        <div
                            v-for="(image, index) in product.images"
                            :key="index"
                            ref="elements"
                            class="slide"
                            :class="classSlide"
                            :style="getStyleForSlide(index)"
                        >
                            <Image
                                v-bind="
                                    getGenericImageProps({
                                        size: image.large_x2,
                                        alt: getImageAlt(product, index),
                                        loading: 'eager',
                                    })
                                "
                            />
                        </div>
                    </div>

                    <div
                        v-if="isThreeSixtyVisible"
                        class="three-sixty-wrapper"
                    >
                        <ThreeSixty
                            v-if="threeSixty.length > 0"
                            :images="threeSixty"
                        />

                        <div class="legend">
                            {{ rotateLabel }}
                        </div>
                    </div>

                    <LabelLabGrown
                        v-if="isLabGrown"
                        class="label-lab-grown"
                    />
                </div>

                <div
                    class="navigation"
                    :class="classNavigation"
                >
                    <div
                        v-for="index in realPageCount"
                        :key="index"
                        :class="getClassDot(index)"
                        class="dot"
                        @click.prevent.stop="goToImage(index - 1)"
                    ></div>
                </div>
            </template>
        </ModalWithHeader>
    </Fixed>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, toRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import type { ProductGalleryFragment } from '@/graphql/api'
import { getIsLabGrown } from '@/domain/product/getIsLabGrown'
import { assertNever } from '@/helpers'
import { useCarousel } from '../carousels/base/useCarouselNew'
import { useCarouselDotsPage } from '../carousels/base/useCarouselDotsPage'
import { useIsMobileAgent } from '@/helpers/detectBrowser'
import { getGenericImageProps } from '@/components/useImageProps'

import ThVideo from '@/components/ThVideo.vue'
import Fixed from '../modal/Fixed.vue'
import ModalWithHeader from '../modal/ModalWithHeader.vue'
import Image from '@/components/Image.vue'
import ThreeSixty from '../three-sixty/ThreeSixty.vue'
import LabelLabGrown from '../LabelLabGrown.vue'
import { useWindowSize } from '@vueuse/core'
import {
    useSwipeToClose,
    useSwipeToCloseStyle,
} from '@/composables/motion/swipe-to-close.ts'
import { usePinchClose } from '@/composables/motion/pinch-to-close.ts'

import { getCssTransition } from '@/composables/motion/css-transition.ts'
import { useNiceGuyZoomer } from '@/composables/motion/nice-guy-zoomer.ts'

const props = defineProps<{
    product: ProductGalleryFragment
    currentExternal?: number
}>()

const emit = defineEmits<{
    (e: 'close'): void
    (e: 'current', v: number): void
}>()

const close = () => emit('close')

const isLabGrown = computed(() => getIsLabGrown(props.product))

const video = computed(() =>
    'video' in props.product ? props.product.video : null
)

const threeSixty = computed(() =>
    'images360' in props.product ? props.product.images360 : []
)
const isThreeSixtyVisible = ref(false)

const switchToThreeSixty = () => {
    if (threeSixty.value.length === 0) {
        return
    }

    isThreeSixtyVisible.value = true
}
const switchToGallery = () => (isThreeSixtyVisible.value = false)
const toggleViewer = () => {
    if (isThreeSixtyVisible.value) {
        switchToGallery()
    } else {
        switchToThreeSixty()
    }
}

const getImageAlt = (ring: ProductGalleryFragment, index: number | '' = '') => {
    switch (ring.__typename) {
        case 'CatalogueEngagementRing':
            // TODO translate
            return `Taylor & Hart ${ring.productName} ${ring.centreStoneConstraints.shape.translation.name} Engagement Ring ${index}`
        case 'CatalogueReadyToGoRing':
            // TODO translate
            return `Taylor &amp; Hart ${ring.productName} Ready to Go Ring ${index}`
        case 'CatalogueWeddingRing':
            // TODO translate
            return `Taylor & Hart ${ring.productName} Wedding Ring ${index}`
        case 'CatalogueJewelleryEarrings':
        // fallthrough
        case 'CatalogueJewelleryBracelet':
        // fallthrough
        case 'CatalogueJewelleryNecklace':
        // fallthrough
        case 'CatalogueJewelleryRing':
            return `Taylor & Hart ${ring.productName} Jewellery ${index}`
        default:
            // TODO translate
            return assertNever(ring, 'Taylor & Hart Ring')
    }
}

const { slideWidth, container, elements, current } = useCarousel(
    toRef(() => props.product.images)
)

const { pageCount, getClassDot, goToPage } = useCarouselDotsPage(
    container,
    slideWidth,
    computed(() => props.product.images),
    current
)
const realPageCount = computed((): number => {
    if (video.value) {
        return pageCount.value + 1
    }

    return pageCount.value
})

onMounted(() => {
    if (undefined !== props.currentExternal) {
        goToImage(props.currentExternal)

        watch(current, (c) => {
            if (c !== props.currentExternal) {
                emit('current', c)
            }
        })
    }
})

function goToImage(index: number | 'three-sixty'): void {
    if (index === 'three-sixty') {
        switchToThreeSixty()
    } else {
        switchToGallery()

        nextTick(() => goToPage(index + 1))
    }
}

const { t } = useI18n()
const { isMobileAgent } = useIsMobileAgent()

const rotateLabel = computed(() =>
    isMobileAgent ? t('gallery.swipe_to_rotate') : t('gallery.drag_to_rotate')
)

const classNavigation = computed(() => ({
    'hidden': isThreeSixtyVisible.value,
}))

const windowSize = useWindowSize()

const {
    scale,
    style,
    transformStyle,
    cssTransitions,
    isZoomed,
    isRotated,
    swipe,
    isSwiping,
    isPinching,
    setScale,
} = useNiceGuyZoomer({
    container,
    minScale: 0.25,
    maxScale: 3,
    initialScale: 1,
    sensitivity: 100,
    disabled: () => current.value === 0 && !!video.value,
})

const pinchCloser = usePinchClose({
    scale,
    isPinching,
    threshold: 0.6,
    close,
    release() {
        if (scale.value < 1) {
            setScale(1, 300)
        }
    },
    transitionTime: 300,
    transitionEasing: 'ease-in-out',
})

const { willClose: willPinchClose } = pinchCloser

const swipeToCloseThreshold = computed(() => {
    return windowSize.height.value * 0.25
})

const swipeToClose = useSwipeToClose({
    swipe,
    isSwiping,
    closeThreshold: swipeToCloseThreshold,
    close,
})

const { willClose: willSwipeClose } = swipeToClose

const willClose = computed(() => willSwipeClose.value || willPinchClose.value)

const swipeToCloseStyle = useSwipeToCloseStyle(swipeToClose, {
    translateMode: 'y',
})

function getStyleForSlide(index: number): string | undefined {
    if (video.value) {
        index++
    }

    if (index !== current.value) {
        return undefined
    }

    if (swipeToCloseStyle.value) {
        return swipeToCloseStyle.value
    }

    if (
        pinchCloser.percentClosed.value > 0 ||
        pinchCloser.isTransitioning.value
    ) {
        const transition = getCssTransition([
            ...cssTransitions.value,
            pinchCloser.transition.value,
        ])

        const styles: string[] = []

        if (transformStyle.value) {
            styles.push(transformStyle.value)
        }

        styles.push(pinchCloser.opacityStyle.value)

        if (transition) {
            styles.push(transition)
        }

        return styles.join('; ')
    }

    return style.value
}

const zoomed = computed(
    () => isZoomed.value || isRotated.value || pinchCloser.isTransitioning.value
)

const classSlide = computed(() => {
    return {
        'zoomed': zoomed.value,
    }
})

const styleContainer = computed(() =>
    zoomed.value ? 'overflow-x: hidden' : 'overflow-x: auto'
)
</script>

<style lang="scss" scoped>
.gallery-modal {
    overscroll-behavior-y: contain;

    :deep(.header) {
        background-color: transparent !important;

        height: 80px;
    }

    .modal-with-header {
        overscroll-behavior-y: contain;

        padding: 0;
        background-color: var(--color-white);

        :deep(.modal-content-wrapper) {
            height: 100%;
            display: flex;
            flex-direction: column;
        }

        .viewer {
            overscroll-behavior-y: contain;

            width: 100%;
            // height: auto;
            height: 100%;
            // aspect-ratio: 1/1;
            display: flex;
            align-items: center;
            position: relative;
            margin-bottom: var(--spacing-8-plus);

            .three-sixty-wrapper {
                overscroll-behavior-y: contain;

                position: absolute;
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                background-color: $white;

                .legend {
                    position: absolute;
                    bottom: var(--spacing-8);
                    text-align: center;
                    font-family: $effra-font-stack;
                    letter-spacing: 0.02em;
                    text-transform: uppercase;
                    font-weight: 400;
                    font-size: 0.75rem;
                    color: $grey-400;

                    &::before {
                        content: '';
                        background: url('/src/assets/icons/swipe-icon.svg')
                            no-repeat center center;
                        background-size: 30px auto;
                        width: 100%;
                        height: 30px;
                        display: block;
                        opacity: 0.25;
                    }
                }
            }

            .carousel {
                overscroll-behavior-y: contain;

                display: flex;
                align-items: center;
                overflow: auto;
                scroll-snap-type: x mandatory;
                width: 100%;
                height: 100%;

                -ms-overflow-style: none; /* IE and Edge */
                scrollbar-width: none; /* Firefox */

                &::-webkit-scrollbar {
                    display: none;
                }

                .slide {
                    scroll-snap-align: start;
                    flex: 0 0 100%;
                    height: auto;
                    user-select: none;
                    line-height: 0;

                    &.zoomed {
                        scroll-snap-align: none;
                    }
                }
            }

            .label-lab-grown {
                position: absolute;
                top: var(--card-padding);
                right: var(--card-padding);
            }

            .release-to-close {
                position: absolute;
                bottom: 50%;
                left: 0;
                right: 0;
                padding: 0 20px;
                font-family: $effra-font-stack;
                font-size: 0.8rem;
                line-height: 20px;
                letter-spacing: 0.05em;
                text-align: center;
                text-transform: uppercase;
                color: $slate;

                opacity: 0;
                transition: opacity 0.5s ease-in-out;
                z-index: -5;

                .text {
                    background-color: $grey-200;
                    padding: 16px 0;
                    border-radius: 26px;
                }
            }
        }

        .switch {
            overscroll-behavior-y: contain;

            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            padding: var(--card-padding);

            .button {
                margin-right: var(--spacing-16);
                height: var(--button-small-height);
                font-family: $effra-font-stack;
                font-size: 0.75rem;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                font-weight: 400;
                color: var(--color-blue);
                border: 1px solid var(--color-blue);
                background-color: transparent;
                user-select: none;
                cursor: pointer;
                display: flex;
                align-items: center;
                border-radius: 40px;
                padding: var(--spacing-16);

                &.active {
                    text-decoration: none;
                    color: var(--color-grey-400);
                    pointer-events: none;
                }
            }
        }

        .navigation {
            position: absolute;
            bottom: 0;
            display: flex;
            justify-content: center;
            align-items: center;
            width: 100%;
            padding: var(--card-padding);

            &.hidden {
                opacity: 0;
                pointer-events: none;
            }

            .dot {
                flex: 0 0 7px;
                height: 7px;
                background-color: $grey-200;
                margin: 4px;
                border-radius: 50%;
                cursor: pointer;
                transition: background-color 80ms ease-in-out;

                &.active {
                    background-color: var(--color-blue);
                }
            }
        }
    }
}
</style>
